import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AdSenseSparkline from "../components/AdSenseSparkline";
import { Plus, Edit3, Trash2, Eye, MousePointer2, Megaphone, XCircle, Power, Mail, CheckCircle2, Ban, Inbox, Clock, FileText, ExternalLink, AlertTriangle, RefreshCcw, DollarSign, TrendingUp, Rss } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Kept in sync with /advertise/guidelines · REJECTED list — surfaced here so admins
// can one-click a canonical reason instead of typing it every time.
const PRESET_REJECT_REASONS = [
  "Broken destination link — the URL doesn't open a working, published page.",
  "Copy contains prohibited content (adult, gambling, tobacco, drugs or weapons).",
  "Health, medical or financial claim without a Zambian regulator's approval on file.",
  "Cryptocurrency scheme promising guaranteed returns — not permitted.",
  "Imagery appears to be copyrighted material the advertiser does not license.",
  "Impersonates another EasyStay host, agent or partner brand.",
  "Headline uses click-bait / all-caps / misleading urgency ('act now', 'you won').",
];

const EMPTY = {
  advertiser: "", headline: "", body: "", cta_text: "Learn more",
  link_url: "https://", accent_color: "#C85A17", placement: "top", is_active: true,
  image_path: "", video_url: "",
  starts_at: "", ends_at: "",
};

const TABS = [
  { key: "pending", label: "Pending", icon: Clock },
  { key: "approved", label: "Approved", icon: CheckCircle2 },
  { key: "rejected", label: "Rejected", icon: Ban },
  { key: "all", label: "All", icon: Inbox },
];

// Convert a UTC ISO string ↔ the value expected by <input type="datetime-local"> (browser-local).
function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  } catch { return ""; }
}
function fromLocalInput(local) {
  if (!local) return "";
  try {
    const d = new Date(local); // treats the string as local time
    if (isNaN(d)) return "";
    return d.toISOString();
  } catch { return ""; }
}

export default function AdminAds() {
  const { user } = useAuth();
  const [ads, setAds] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [tab, setTab] = useState("pending");
  const [editing, setEditing] = useState(null); // ad object or {} for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [rejecting, setRejecting] = useState(null); // ad object
  const [rejectReason, setRejectReason] = useState("");
  const [deleting, setDeleting] = useState(null);    // ad object
  const [busyId, setBusyId] = useState(null);  const load = useCallback(() => {
    const params = tab === "all" ? {} : { params: { status: tab } };
    axios.get(`${API}/admin/ads`, params)
      .then(({ data }) => setAds(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Load failed"));
    axios.get(`${API}/admin/ads/counts`)
      .then(({ data }) => setCounts(data))
      .catch(() => {});
  }, [tab]);
  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const openNew = () => { setForm({ ...EMPTY }); setEditing({}); };
  const openEdit = (ad) => { setForm({ ...ad }); setEditing(ad); };
  const close = () => { setEditing(null); };

  const save = async () => {
    if (!form.advertiser || !form.headline || !form.link_url) return toast.error("Advertiser, headline and link are required");
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/admin/ads/${editing.id}`, form);
        toast.success("Ad updated");
      } else {
        await axios.post(`${API}/admin/ads`, form);
        toast.success("Ad created");
      }
      close(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (ad) => {
    try {
      await axios.put(`${API}/admin/ads/${ad.id}`, { ...ad, is_active: !ad.is_active });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = (ad) => setDeleting(ad);
  const closeDelete = () => setDeleting(null);
  const confirmDelete = async () => {
    if (!deleting) return;
    setBusyId(deleting.id);
    try {
      await axios.delete(`${API}/admin/ads/${deleting.id}`);
      toast.success("Ad deleted permanently");
      closeDelete(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const approve = async (ad) => {
    // Lightweight T&Cs confirm — spelled out so it's clear the admin owns the decision.
    if (!window.confirm(
      `Approve "${ad.headline || ad.advertiser}"?\n\n` +
      `By approving you confirm this ad meets EasyStay's ad content guidelines ` +
      `(easystayzm.com/advertise/guidelines). The ad will go live immediately.`
    )) return;
    setBusyId(ad.id);
    try {
      await axios.post(`${API}/admin/ads/${ad.id}/approve`);
      toast.success(`Approved · ${ad.advertiser} is now live`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusyId(null); }
  };

  const openReject = (ad) => { setRejecting(ad); setRejectReason(""); };
  const closeReject = () => { setRejecting(null); setRejectReason(""); };
  const applyPreset = (text) => {
    setRejectReason((prev) => (prev.trim() ? prev.trim() + "\n" + text : text));
  };
  const confirmReject = async () => {
    if (!rejecting) return;
    setBusyId(rejecting.id);
    try {
      await axios.post(`${API}/admin/ads/${rejecting.id}/reject`, { reason: rejectReason });
      toast.success("Rejected · submitter has been notified");
      closeReject(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusyId(null); }
  };

  const sendReportNow = async () => {
    if (!window.confirm("Send the weekly ad report email now to every admin?")) return;
    setSendingReport(true);
    try {
      const { data } = await axios.post(`${API}/admin/ad-report/send-now`);
      if (data.successes > 0) {
        toast.success(`Report sent to ${data.successes} admin(s)`);
      } else if (data.failures?.length) {
        toast.error(`Send failed: ${data.failures[0]?.reason || "unknown"}`);
      } else {
        toast.info("No admin recipients found");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send report");
    } finally { setSendingReport(false); }
  };

  // ------ AdSense snapshot logging ------
  const [adsenseSnaps, setAdsenseSnaps] = useState([]);
  const [adsenseOpen, setAdsenseOpen] = useState(false);
  const [adsenseSaving, setAdsenseSaving] = useState(false);
  const _mondayThisWeek = () => {
    const d = new Date();
    const day = (d.getDay() + 6) % 7;  // 0 = Mon
    d.setDate(d.getDate() - day);
    return d.toISOString().slice(0, 10);
  };
  const _sundayThisWeek = () => {
    const d = new Date(_mondayThisWeek());
    d.setDate(d.getDate() + 6);
    return d.toISOString().slice(0, 10);
  };
  const ADSENSE_EMPTY = {
    week_start: _mondayThisWeek(),
    week_end: _sundayThisWeek(),
    impressions: 0,
    clicks: 0,
    estimated_revenue_usd: 0,
  };
  const [adsenseForm, setAdsenseForm] = useState(ADSENSE_EMPTY);
  const [adsenseTrend, setAdsenseTrend] = useState(null);

  const loadAdsense = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/adsense/snapshots`, { params: { limit: 12 } });
      setAdsenseSnaps(data);
    } catch { /* non-critical */ }
    try {
      const { data } = await axios.get(`${API}/admin/adsense/trend`, { params: { weeks: 8 } });
      setAdsenseTrend(data);
    } catch { setAdsenseTrend(null); }
  }, []);
  useEffect(() => { if (user?.is_admin) loadAdsense(); }, [user?.is_admin, loadAdsense]);

  const submitAdsense = async () => {
    if (adsenseForm.impressions < 0 || adsenseForm.clicks < 0 || adsenseForm.estimated_revenue_usd < 0) {
      toast.error("Numbers must be non-negative");
      return;
    }
    setAdsenseSaving(true);
    try {
      await axios.post(`${API}/admin/adsense/snapshot`, {
        ...adsenseForm,
        impressions: parseInt(adsenseForm.impressions) || 0,
        clicks: parseInt(adsenseForm.clicks) || 0,
        estimated_revenue_usd: parseFloat(adsenseForm.estimated_revenue_usd) || 0,
      });
      toast.success("AdSense snapshot logged");
      setAdsenseForm(ADSENSE_EMPTY);
      setAdsenseOpen(false);
      loadAdsense();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setAdsenseSaving(false); }
  };

  const deleteAdsense = async (id) => {
    if (!window.confirm("Delete this AdSense snapshot? It will no longer appear in the weekly report.")) return;
    try {
      await axios.delete(`${API}/admin/adsense/snapshot/${id}`);
      toast.success("Snapshot deleted");
      loadAdsense();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const latestAdsense = adsenseSnaps[0] || null;

  // ------ AdSense OAuth auto-sync ------
  const [adsenseConn, setAdsenseConn] = useState(null);
  const [adsenseSyncing, setAdsenseSyncing] = useState(false);
  const loadAdsenseConnection = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/adsense/connection`);
      setAdsenseConn(data);
    } catch { setAdsenseConn(null); }
  }, []);
  useEffect(() => { if (user?.is_admin) loadAdsenseConnection(); }, [user?.is_admin, loadAdsenseConnection]);

  // Show a toast if the OAuth callback bounced back with ?adsense=ok|err
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const flag = p.get("adsense");
    if (flag === "ok") { toast.success("AdSense connected · weekly sync scheduled"); loadAdsenseConnection(); }
    if (flag === "err") { toast.error("AdSense connection failed. Check the callback URL in Google Cloud Console."); }
    if (flag) { p.delete("adsense"); window.history.replaceState(null, "", `${window.location.pathname}${p.toString() ? "?" + p.toString() : ""}`); }
  }, [loadAdsenseConnection]);

  const connectAdsense = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/adsense/oauth/start`);
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start OAuth"); }
  };
  const syncAdsenseNow = async () => {
    setAdsenseSyncing(true);
    try {
      const { data } = await axios.post(`${API}/admin/adsense/sync-now`);
      if (data.ok) {
        const s = data.snapshot;
        toast.success(`Synced · $${Number(s.estimated_revenue_usd).toFixed(2)} (K${Number(s.estimated_revenue_zmw).toFixed(2)}) for ${s.week_start}`);
        loadAdsense();
      } else {
        toast.error(data.reason || "Sync failed");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setAdsenseSyncing(false); }
  };
  const disconnectAdsense = async () => {
    if (!window.confirm("Disconnect Google AdSense? The weekly auto-sync will stop until you reconnect. Historical snapshots stay put.")) return;
    try {
      await axios.delete(`${API}/admin/adsense/connection`);
      toast.success("AdSense disconnected");
      loadAdsenseConnection();
    } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
  };

  // Diagnose the AdSense OAuth setup — tells the admin exactly which of the
  // Google Cloud Console steps still need to happen (API enable / redirect
  // URIs / test user) without them having to guess from Google's error pages.
  const [diagnoseOpen, setDiagnoseOpen] = useState(false);
  const [diagnoseData, setDiagnoseData] = useState(null);
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  const runDiagnose = async () => {
    setDiagnoseLoading(true);
    setDiagnoseData(null);
    setDiagnoseOpen(true);
    try {
      const { data } = await axios.get(`${API}/admin/adsense/health-check`);
      setDiagnoseData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Health check failed");
      setDiagnoseData({ checks: [], ready: false, error: e.response?.data?.detail || String(e) });
    } finally { setDiagnoseLoading(false); }
  };

  // Weekly digest email preview — loaded into an iframe inside a Dialog so
  // admins can see the exact Mon-morning render without triggering an actual send.
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewSrc = `${API}/admin/ad-report/preview.html?t=${previewOpen ? Date.now() : ""}`;

  if (!user) return <div className="mx-auto max-w-3xl px-6 py-16 text-center text-slate-500">Please sign in.</div>;
  if (!user.is_admin) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <XCircle className="mx-auto h-12 w-12 text-rose-500" />
        <h1 className="mt-4 font-display text-2xl font-bold">Admin only</h1>
      </div>
    );
  }

  const totalImp = ads.reduce((a, x) => a + (x.impressions || 0), 0);
  const totalClicks = ads.reduce((a, x) => a + (x.clicks || 0), 0);
  const ctr = totalImp ? ((totalClicks / totalImp) * 100).toFixed(2) : "0.00";
  // Renewal analytics: how often are advertisers using the one-click renewal, and which cadence is popular?
  const totalRenewals = ads.reduce((a, x) => a + (x.renewal_count || 0), 0);
  const renewalDaysCount = ads.reduce((acc, x) => {
    (x.renewal_days_history || []).forEach((d) => { acc[d] = (acc[d] || 0) + 1; });
    return acc;
  }, {});
  const renewalMix = [7, 14, 30]
    .map((d) => `+${d}d ${renewalDaysCount[d] || 0}`)
    .join(" · ");

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Advertisements</h1>
          <p className="mt-1 text-slate-600">Manage promotional strips shown at the top and bottom of every page. Any product category is welcome — telecom, retail, beverage, electronics, hospitality, and more.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/advertise/guidelines"
            target="_blank"
            data-testid="view-guidelines-btn"
            className="btn-outline text-sm"
            title="Open the public ad content guidelines / T&Cs in a new tab"
          >
            <FileText className="mr-1 inline h-4 w-4" /> View guidelines
          </Link>
          <button
            onClick={() => setPreviewOpen(true)}
            data-testid="preview-report-btn"
            className="btn-outline text-sm"
            title="Preview the exact HTML that Monday's weekly digest will send"
          >
            <Eye className="mr-1 inline h-4 w-4" /> Preview email
          </button>
          <button
            onClick={sendReportNow}
            disabled={sendingReport}
            data-testid="send-weekly-report-btn"
            className="btn-outline text-sm"
            title="Email the last-7-days ad report to every admin now (also runs automatically Monday 08:00 Africa/Lusaka)"
          >
            <Mail className="mr-1 inline h-4 w-4" /> {sendingReport ? "Sending…" : "Send weekly report"}
          </button>
          <button onClick={openNew} data-testid="new-ad-btn" className="btn-primary text-sm">
            <Plus className="mr-1 inline h-4 w-4" /> New ad
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200">
        {TABS.map((t) => {
          const c = t.key === "all" ? counts.total : counts[t.key];
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`ads-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold transition ${
                active ? "border-copper text-copper" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              <span className={`ml-1 rounded-full px-2 py-0.5 text-[11px] ${active ? "bg-copper/10 text-copper" : "bg-slate-100 text-slate-600"}`}>{c ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* Metrics */}
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Total impressions", value: totalImp, icon: Eye },
          { label: "Total clicks", value: totalClicks, icon: MousePointer2 },
          { label: "Overall CTR", value: `${ctr}%`, icon: Megaphone },
          { label: "One-click renewals", value: totalRenewals, sub: renewalMix, icon: RefreshCcw },
        ].map((m) => (
          <div key={m.label} className="rounded-3xl bg-white p-6 card-shadow">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-copper/10 text-copper">
                <m.icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-slate-500">{m.label}</div>
                <div className="font-display text-2xl font-bold text-slate-900">{m.value}</div>
                {m.sub && <div className="mt-0.5 truncate font-mono text-[10px] text-slate-500">{m.sub}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* AdSense side-by-side channel card — auto-sync via Google OAuth when
          connected, otherwise falls back to the manual "Log AdSense" dialog. */}
      <div
        data-testid="adsense-card"
        className="mt-4 rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-slate-50 p-5 card-shadow"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-forest font-bold">
              <TrendingUp className="h-3.5 w-3.5" /> Google AdSense · latest snapshot
              {adsenseConn?.connected && (
                <span className="ml-2 rounded-full bg-forest/10 px-2 py-0.5 text-[10px] font-semibold text-forest" data-testid="adsense-connected-badge">
                  Auto-sync ON
                </span>
              )}
            </div>
            {latestAdsense ? (
              <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <div className="font-display text-2xl font-bold text-forest">
                  ${Number(latestAdsense.estimated_revenue_usd).toFixed(2)}
                </div>
                <div className="text-sm text-slate-500">
                  ≈ K{Number(latestAdsense.estimated_revenue_zmw).toFixed(2)}
                </div>
                <div className="text-xs text-slate-500">
                  {Number(latestAdsense.impressions).toLocaleString()} impr ·{" "}
                  {Number(latestAdsense.clicks).toLocaleString()} clicks · CTR {latestAdsense.ctr}%
                </div>
                <div className="text-xs text-slate-400">
                  week of {latestAdsense.week_start}
                  {latestAdsense.source === "auto" && <span className="ml-1 text-forest">· auto</span>}
                </div>
              </div>
            ) : (
              <p className="mt-2 text-sm text-slate-500">
                {adsenseConn?.connected
                  ? "Connected but no snapshots yet. Click Sync now to pull last week's data."
                  : "Connect Google AdSense to auto-pull weekly numbers, or click Log AdSense to backfill manually."}
              </p>
            )}
            {adsenseConn?.connected && adsenseConn?.last_synced_at && (
              <p className="mt-1 text-[11px] text-slate-400">
                Last sync: {new Date(adsenseConn.last_synced_at).toLocaleString()} · account {adsenseConn.display_name || adsenseConn.publisher_id}
              </p>
            )}
            {adsenseTrend && adsenseTrend.count >= 2 && (
              <div className="mt-3">
                <AdSenseSparkline
                  points={adsenseTrend.points}
                  deltaPct={adsenseTrend.delta_pct}
                />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {adsenseConn?.connected ? (
              <>
                <button
                  onClick={syncAdsenseNow}
                  disabled={adsenseSyncing}
                  data-testid="sync-adsense-btn"
                  className="btn-primary text-sm !bg-forest hover:!bg-forest/90"
                >
                  <RefreshCcw className={`mr-1 inline h-4 w-4 ${adsenseSyncing ? "animate-spin" : ""}`} />
                  {adsenseSyncing ? "Syncing…" : "Sync now"}
                </button>
                <button
                  onClick={disconnectAdsense}
                  data-testid="disconnect-adsense-btn"
                  className="btn-outline text-sm !border-rose-200 !text-rose-600"
                >
                  Disconnect
                </button>
              </>
            ) : adsenseConn?.configured ? (
              <button
                onClick={connectAdsense}
                data-testid="connect-adsense-btn"
                className="btn-primary text-sm !bg-forest hover:!bg-forest/90"
              >
                <DollarSign className="mr-1 inline h-4 w-4" /> Connect Google AdSense
              </button>
            ) : (
              <div className="text-xs text-rose-600" data-testid="adsense-oauth-missing">
                OAuth env vars missing — set GOOGLE_ADSENSE_CLIENT_ID / SECRET / TOKEN_ENCRYPTION_KEY
              </div>
            )}
            <button
              onClick={runDiagnose}
              data-testid="diagnose-adsense-btn"
              className="btn-outline text-sm"
              title="Run a live checklist against Google to see what's still needed in Cloud Console"
            >
              <AlertTriangle className="mr-1 inline h-4 w-4" /> Diagnose
            </button>
            <button
              onClick={() => setAdsenseOpen(true)}
              data-testid="log-adsense-btn"
              className="btn-outline text-sm"
              title="Backfill an older week manually"
            >
              <DollarSign className="mr-1 inline h-4 w-4" /> Log AdSense
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="mt-8 space-y-4">
        {ads.length === 0 ? (
          <div className="rounded-3xl bg-white p-10 text-center">
            <Megaphone className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-slate-500">No ads yet. Create your first promotional strip.</p>
          </div>
        ) : ads.map((a) => {
          const rowCtr = a.impressions ? ((a.clicks / a.impressions) * 100).toFixed(2) : "0.00";
          const status = a.status || "approved";
          const statusClass =
            status === "pending" ? "bg-amber-100 text-amber-800 ring-amber-200"
            : status === "approved" ? "bg-green-100 text-green-800 ring-green-200"
            : "bg-rose-100 text-rose-800 ring-rose-200";
          return (
            <div key={a.id} data-testid={`ad-row-${a.id}`} className="rounded-3xl bg-white p-5 card-shadow">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${a.is_active ? "bg-green-500" : "bg-slate-300"}`} />
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{a.advertiser}</span>
                    <span className="chip !bg-slate-100 !text-slate-700 capitalize">{a.placement} strip</span>
                    <span data-testid={`ad-status-${a.id}`}
                      className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ring-1 ${statusClass}`}>
                      {status}
                    </span>
                    {(a.starts_at || a.ends_at) && (
                      <span data-testid={`ad-schedule-${a.id}`}
                        className="flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-indigo-800 ring-1 ring-indigo-200">
                        <Clock className="h-3 w-3" />
                        {a.starts_at ? new Date(a.starts_at).toLocaleDateString() : "—"}
                        {" → "}
                        {a.ends_at ? new Date(a.ends_at).toLocaleDateString() : "—"}
                      </span>
                    )}
                  </div>
                  {/* Live preview */}
                  <div className="mt-3 overflow-hidden rounded-xl" style={{ background: a.accent_color }}>
                    <div className="flex items-center gap-3 px-4 py-2 text-sm">
                      <span className="rounded-full bg-black/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">Sponsored</span>
                      <div className="flex-1 truncate font-semibold text-white">
                        <span className="text-white/70 mr-1">{a.advertiser} ·</span>{a.headline}
                        {a.body && <span className="ml-2 hidden text-white/80 md:inline">{a.body}</span>}
                      </div>
                      <span className="rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-slate-900">{a.cta_text}</span>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                    <span>Link: <a href={a.link_url} target="_blank" rel="noreferrer" className="text-copper hover:underline">{a.link_url}</a></span>
                    <span>Impressions: <span className="font-semibold text-slate-900">{a.impressions || 0}</span></span>
                    <span>Clicks: <span className="font-semibold text-slate-900">{a.clicks || 0}</span></span>
                    <span>CTR: <span className="font-semibold text-slate-900">{rowCtr}%</span></span>
                    {(a.renewal_count || 0) > 0 && (
                      <span data-testid={`ad-renewals-${a.id}`}>
                        Renewals: <span className="font-semibold text-slate-900">{a.renewal_count}×</span>
                        <span className="ml-1 font-mono text-[10px] text-slate-500">({(a.renewal_days_history || []).map((d) => `+${d}d`).join(", ")})</span>
                      </span>
                    )}
                  </div>
                  {(a.submitted_by_email || a.submitted_by_name) && (
                    <div className="mt-2 text-xs text-slate-500">
                      Submitted by <span className="font-semibold text-slate-900">{a.submitted_by_name || "—"}</span>
                      {a.submitted_by_email && <> · <a href={`mailto:${a.submitted_by_email}`} className="text-copper hover:underline">{a.submitted_by_email}</a></>}
                      {a.submitted_by_phone && <> · {a.submitted_by_phone}</>}
                    </div>
                  )}
                  {status === "rejected" && a.rejection_reason && (
                    <div className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
                      <b>Rejection reason:</b> {a.rejection_reason}
                    </div>
                  )}
                </div>
                <div className="flex flex-shrink-0 flex-wrap gap-2">
                  {status === "pending" && (
                    <>
                      <button onClick={() => approve(a)} disabled={busyId === a.id}
                        data-testid={`approve-${a.id}`}
                        className="btn-primary !py-2 text-xs !bg-green-600 hover:!bg-green-700">
                        <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> Approve
                      </button>
                      <button onClick={() => openReject(a)} disabled={busyId === a.id}
                        data-testid={`reject-${a.id}`}
                        className="btn-outline !py-2 text-xs !border-rose-200 !text-rose-600">
                        <Ban className="mr-1 inline h-3.5 w-3.5" /> Reject
                      </button>
                    </>
                  )}
                  {status !== "pending" && (
                    <button onClick={() => toggleActive(a)} data-testid={`toggle-${a.id}`} className="btn-outline !py-2 text-xs">
                      <Power className="mr-1 inline h-3.5 w-3.5" /> {a.is_active ? "Pause" : "Activate"}
                    </button>
                  )}
                  <button onClick={() => openEdit(a)} data-testid={`edit-${a.id}`} className="btn-outline !py-2 text-xs">
                    <Edit3 className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => remove(a)} data-testid={`delete-${a.id}`} className="btn-outline !py-2 text-xs !border-rose-200 !text-rose-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit ad" : "New ad"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Advertiser</label>
                <input data-testid="ad-advertiser" value={form.advertiser} onChange={(e) => setForm({ ...form, advertiser: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Placement</label>
                <select data-testid="ad-placement" value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white">
                  <option value="top">Top strip</option>
                  <option value="bottom">Bottom strip</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Headline *</label>
              <input data-testid="ad-headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Sub-text (optional)</label>
              <input data-testid="ad-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">CTA text</label>
                <input data-testid="ad-cta" value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Accent colour</label>
                <div className="mt-1 flex items-center gap-2">
                  <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200" />
                  <input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 font-mono text-sm outline-none focus:border-copper focus:bg-white" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Link URL *</label>
              <input data-testid="ad-link" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                placeholder="https://…"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white" />
            </div>

            {form.placement === "top" && (
              <>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Photo (optional)</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      type="file" accept="image/*" data-testid="ad-image-file"
                      onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        try {
                          const fd = new FormData(); fd.append("file", f);
                          const { data } = await axios.post(`${API}/uploads/image`, fd);
                          setForm({ ...form, image_path: data.path, video_url: "" });
                          toast.success("Photo uploaded");
                        } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                      }}
                      className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
                    />
                    {form.image_path && (
                      <button type="button" onClick={() => setForm({ ...form, image_path: "" })} className="btn-outline !py-2 text-xs">Remove</button>
                    )}
                  </div>
                  {form.image_path && (
                    <div className="mt-2 h-20 w-full overflow-hidden rounded-lg">
                      <img src={form.image_path.startsWith("http") ? form.image_path : `${API}/files/${form.image_path}`} alt="" className="h-full w-full object-cover" />
                    </div>
                  )}
                  <div className="mt-1 text-xs text-slate-500">Or paste an image URL:</div>
                  <input value={form.image_path && form.image_path.startsWith("http") ? form.image_path : ""}
                    onChange={(e) => setForm({ ...form, image_path: e.target.value, video_url: "" })}
                    placeholder="https://images.unsplash.com/…"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs outline-none focus:border-copper focus:bg-white" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Video URL (optional, overrides photo)</label>
                  <input data-testid="ad-video" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    placeholder="https://…/promo.mp4"
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs outline-none focus:border-copper focus:bg-white" />
                  <div className="mt-1 text-xs text-slate-500">Direct link to an mp4/webm file. Autoplays muted in the banner.</div>
                </div>
              </>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active (visible to visitors)
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Starts at (optional)</label>
                <input
                  type="datetime-local"
                  data-testid="ad-starts-at"
                  value={toLocalInput(form.starts_at)}
                  onChange={(e) => setForm({ ...form, starts_at: fromLocalInput(e.target.value) })}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Ends at (optional)</label>
                <input
                  type="datetime-local"
                  data-testid="ad-ends-at"
                  value={toLocalInput(form.ends_at)}
                  onChange={(e) => setForm({ ...form, ends_at: fromLocalInput(e.target.value) })}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500">Leave blank to run continuously. Use these to auto-pause seasonal promotions.</p>

            <div className="mt-2 overflow-hidden rounded-xl" style={{ background: form.accent_color }}>
              <div className="flex items-center gap-3 px-4 py-2 text-sm">
                <span className="rounded-full bg-black/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">Sponsored</span>
                <div className="flex-1 truncate font-semibold text-white">
                  <span className="text-white/70 mr-1">{form.advertiser || "Advertiser"} ·</span>{form.headline || "Your headline appears here"}
                </div>
                <span className="rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-slate-900">{form.cta_text}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={close} className="btn-outline text-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-ad-btn" className="btn-primary text-sm">
              {saving ? "Saving…" : "Save ad"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && closeReject()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reject ad · {rejecting?.advertiser}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900">
              <div className="flex items-center gap-2 font-semibold">
                <FileText className="h-4 w-4" /> Ad content guidelines
              </div>
              <p className="mt-1">
                The submitter will receive your reason by email. Cite a specific guideline so they can fix and resubmit.
              </p>
              <Link to="/advertise/guidelines" target="_blank" data-testid="reject-guidelines-link"
                className="mt-1 inline-flex items-center gap-1 text-amber-800 underline underline-offset-2 hover:text-amber-900">
                Open full guidelines <ExternalLink className="h-3 w-3" />
              </Link>
            </div>

            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-slate-500">Quick reasons (tap to add)</div>
              <div className="flex flex-wrap gap-1.5">
                {PRESET_REJECT_REASONS.map((r, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`preset-reason-${i}`}
                    onClick={() => applyPreset(r)}
                    className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-700 hover:bg-copper/15 hover:text-copper"
                  >
                    {r.split(" — ")[0].split(".")[0].slice(0, 44)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Reason (sent to submitter)</label>
              <textarea
                data-testid="reject-reason-input"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={5}
                placeholder="e.g. Link URL not reachable, imagery not appropriate for family audiences, headline exceeds 90 characters…"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
              />
            </div>
          </div>
          <DialogFooter>
            <button onClick={closeReject} className="btn-outline text-sm">Cancel</button>
            <button
              onClick={confirmReject}
              disabled={busyId === rejecting?.id}
              data-testid="confirm-reject-btn"
              className="btn-primary text-sm !bg-rose-600 hover:!bg-rose-700"
            >
              {busyId === rejecting?.id ? "Rejecting…" : "Reject & notify"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog (permanent) */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && closeDelete()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete ad permanently?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <div className="flex items-start gap-2 rounded-xl bg-rose-50 p-3 text-rose-800">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <div>
                This will remove <b>{deleting?.advertiser}</b>&apos;s ad and its impression / click history.
                The action is <b>permanent</b> — the submitter will not receive an email.
              </div>
            </div>
            <p>
              If the ad violates our policies but the submitter should be told, use <b>Reject</b> from the pending tab instead. Delete is intended for spam, duplicates, or cleanup after long-expired campaigns.
            </p>
            <Link to="/advertise/guidelines" target="_blank" data-testid="delete-guidelines-link"
              className="inline-flex items-center gap-1 text-xs text-copper underline underline-offset-2">
              Review ad content guidelines <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          <DialogFooter>
            <button onClick={closeDelete} className="btn-outline text-sm">Cancel</button>
            <button
              onClick={confirmDelete}
              disabled={busyId === deleting?.id}
              data-testid="confirm-delete-btn"
              className="btn-primary text-sm !bg-rose-600 hover:!bg-rose-700"
            >
              {busyId === deleting?.id ? "Deleting…" : "Delete permanently"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AdSense weekly snapshot dialog */}
      <Dialog open={adsenseOpen} onOpenChange={setAdsenseOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Log this week&apos;s AdSense revenue</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Grab the numbers from{" "}
            <a
              href="https://www.google.com/adsense/new/u/0/pub-9126675393426755/main/reports"
              target="_blank"
              rel="noreferrer"
              className="text-copper hover:underline"
            >
              Google AdSense → Reports
            </a>{" "}
            (This week) and paste them here. USD is auto-converted to ZMW at the current rate for the Monday digest.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Week start</label>
              <input
                type="date"
                value={adsenseForm.week_start}
                onChange={(e) => setAdsenseForm({ ...adsenseForm, week_start: e.target.value })}
                data-testid="adsense-week-start"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Week end</label>
              <input
                type="date"
                value={adsenseForm.week_end}
                onChange={(e) => setAdsenseForm({ ...adsenseForm, week_end: e.target.value })}
                data-testid="adsense-week-end"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Impressions</label>
              <input
                type="number" min={0}
                value={adsenseForm.impressions}
                onChange={(e) => setAdsenseForm({ ...adsenseForm, impressions: e.target.value })}
                data-testid="adsense-impressions"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Clicks</label>
              <input
                type="number" min={0}
                value={adsenseForm.clicks}
                onChange={(e) => setAdsenseForm({ ...adsenseForm, clicks: e.target.value })}
                data-testid="adsense-clicks"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-widest text-slate-500">Estimated revenue (USD)</label>
              <input
                type="number" min={0} step="0.01"
                value={adsenseForm.estimated_revenue_usd}
                onChange={(e) => setAdsenseForm({ ...adsenseForm, estimated_revenue_usd: e.target.value })}
                data-testid="adsense-revenue-usd"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
              />
            </div>
          </div>
          {adsenseSnaps.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-slate-500">Recent snapshots</div>
              <div className="mt-2 max-h-56 space-y-2 overflow-y-auto">
                {adsenseSnaps.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`adsense-row-${s.id}`}
                    className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900">
                        ${Number(s.estimated_revenue_usd).toFixed(2)}
                        <span className="ml-2 font-normal text-slate-500">≈ K{Number(s.estimated_revenue_zmw).toFixed(2)}</span>
                      </div>
                      <div className="text-slate-500">
                        {Number(s.impressions).toLocaleString()} impr · {Number(s.clicks).toLocaleString()} clicks · {s.week_start} → {s.week_end}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteAdsense(s.id)}
                      data-testid={`delete-adsense-${s.id}`}
                      className="rounded-full p-1 text-rose-500 hover:bg-rose-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setAdsenseOpen(false)} className="btn-outline text-sm">Cancel</button>
            <button
              onClick={submitAdsense}
              disabled={adsenseSaving}
              data-testid="submit-adsense-btn"
              className="btn-primary text-sm"
            >
              {adsenseSaving ? "Saving…" : "Save snapshot"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Weekly digest email preview — same HTML that will be blasted on Monday */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-copper" />
              Monday digest preview
            </DialogTitle>
            <p className="text-xs text-slate-500">
              Live render of <code className="rounded bg-slate-100 px-1.5 py-0.5">/api/admin/ad-report/preview.html</code> — this is exactly what admins will receive at 08:00 Africa/Lusaka.
            </p>
          </DialogHeader>
          <iframe
            src={previewSrc}
            title="Weekly digest preview"
            data-testid="preview-report-iframe"
            className="flex-1 w-full bg-[#f9f6f0]"
            sandbox="allow-same-origin"
          />
        </DialogContent>
      </Dialog>

      {/* AdSense OAuth diagnostic modal — live checklist of what still needs
          to happen in Google Cloud Console, with copy-paste-friendly fix hints. */}
      <Dialog open={diagnoseOpen} onOpenChange={setDiagnoseOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-copper" /> AdSense setup diagnosis
            </DialogTitle>
            <p className="text-xs text-slate-500">
              Live tests against your env vars + Google&apos;s servers. Fix any red step and re-run.
            </p>
          </DialogHeader>
          {diagnoseLoading && (
            <div className="py-8 text-center text-sm text-slate-500" data-testid="diagnose-loading">
              Running checks against Google…
            </div>
          )}
          {diagnoseData && (
            <div className="space-y-2" data-testid="diagnose-results">
              <div className={`flex items-center gap-2 rounded-2xl p-3 text-sm font-semibold ${
                diagnoseData.ready ? "bg-emerald-50 text-forest" : "bg-amber-50 text-amber-700"
              }`}>
                {diagnoseData.ready ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                {diagnoseData.ready
                  ? "Everything green — auto-sync is ready."
                  : `${diagnoseData.checks.filter((c) => !c.ok).length} step(s) still need attention.`}
              </div>
              {diagnoseData.checks.map((c, i) => (
                <div
                  key={i}
                  data-testid={`diagnose-step-${i}`}
                  className={`rounded-xl border p-3 ${c.ok ? "border-emerald-100 bg-emerald-50/40" : "border-rose-100 bg-rose-50/40"}`}
                >
                  <div className="flex items-start gap-2">
                    {c.ok
                      ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-forest" />
                      : <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-500" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900">{c.step}</div>
                      {c.fix && (
                        <div className="mt-1 text-xs text-slate-600">
                          <b>Fix:</b> {c.fix}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {diagnoseData.connected_publisher && (
                <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                  Connected publisher: <code>{diagnoseData.connected_publisher}</code>
                  {diagnoseData.last_synced_at && <> · last sync {new Date(diagnoseData.last_synced_at).toLocaleString()}</>}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={runDiagnose} className="btn-outline text-sm">
              <RefreshCcw className="mr-1 inline h-3.5 w-3.5" /> Re-run
            </button>
            <button onClick={() => setDiagnoseOpen(false)} className="btn-primary text-sm">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
